import styles from './Menu.module.scss';
import {
  ReactNode,
  useEffect,
  FocusEvent,
  useMemo,
  useState,
  useCallback,
} from 'react';
import { MenuContext, MenuOption } from './MenuContext';
import { MenuActionsContext } from './MenuActionsContext';

interface Props {
  children: (args: { open?: () => void; opened?: boolean }) => ReactNode;
  options: ReactNode;
}

export function Menu(props: Props) {
  const [opened, setOpened] = useState(false);
  const menuOptionsMap = useMemo(() => new Map<string, MenuOption>(), []);

  const className = [styles['menu'], opened && styles['menu-expanded']]
    .filter(Boolean)
    .join(' ');

  useEffect(() => {
    if (opened) {
      const firstMenuOption = [...menuOptionsMap.values()][0];
      firstMenuOption.ref.current?.focus();
    }
  }, [menuOptionsMap, opened]);

  const close = () => {
    setOpened(false);
  };

  const onBlur = (event: FocusEvent<HTMLDivElement>) => {
    event.stopPropagation();
    const isChildren = event.currentTarget?.contains(event.relatedTarget);
    setTimeout(() => {
      if (!isChildren) {
        setOpened(false);
      }
    }, 1);
  };

  const open = useCallback(() => {
    setOpened(true);
  }, []);

  return (
    <MenuContext.Provider value={menuOptionsMap}>
      <MenuActionsContext.Provider value={{ open, close }}>
        <div className={styles['menu-root']}>
          {props.children({ open, opened })}
          <div className={className} onBlur={onBlur} role="menu">
            {props.options}
          </div>
        </div>
      </MenuActionsContext.Provider>
    </MenuContext.Provider>
  );
}
